import { Popover, PopoverTrigger, PopoverContent } from "~/react/ui/popover"
import emptyStateImageUrl from "~/images/empty_states/bulk.svg"
import { useEffect, useState, useMemo } from "react"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsListItem,
  TabsTrigger,
  TabsTriggerCount,
} from "~/react/ui/tabs"
import {
  OperationsCard,
  OperationsCardHeader,
  OperationsCardDetails,
  OperationsCardActions,
  OperationsCardDetailsActions,
} from "~/react/components/operations/card-component"
import { OperationProvider } from "~/react/components/operations/operation-context"
import Operation from "~/types/serializers/Operation"
import * as ActionCable from "@rails/actioncable"
import {
  leads_company_list_path,
  leads_path,
  operation_path,
  operations_read_path,
  cancel_bulk_verification_path,
  leads_import_path,
  companies_import_path,
  leads_companies_path,
  operation_cancel_path,
} from "~/lib/routes"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"

const EmptyState = ({
  type,
}: {
  type: "all" | "active" | "successful" | "unsuccessful"
}) => {
  return (
    <div className="empty-state operations-panel__content">
      <div className="empty-state__img">
        <img src={emptyStateImageUrl} alt="" height="140" />
      </div>
      <div className="empty-state__content">
        <h3 className="empty-state__title">
          {i18n.t(`js.operations.empty_state.${type}.title`)}
        </h3>
        <p>{i18n.t(`js.operations.empty_state.${type}.description`)}</p>
      </div>
    </div>
  )
}

const cancellableOperations: Operation["kind"][] = [
  "reveal_emails",
  "leads/verify",
]

const renderOperationCard = (
  operation: Operation,
  onExpand: (expanded: boolean) => void,
) => {
  return (
    <OperationProvider
      operation={operation}
      key={operation.id}
      onExpand={onExpand}
    >
      <OperationsCard status={operation.status} read={!!operation.read_at}>
        <OperationsCardHeader>
          <OperationsCardActions>
            {operation.kind === "reveal_emails" && (
              <Button size="xs" className="operation-card__action" asChild>
                <a href={leads_path({ operation_id: operation.id })}>
                  {i18n.t("js.operations.actions.view_in_leads")}
                </a>
              </Button>
            )}
            {operation.kind === "leads/import" && (
              <Button size="xs" className="operation-card__action" asChild>
                <a
                  href={leads_path({
                    leads_list_id: operation.associated_model_metadata?.id,
                    leads_import_id:
                      operation.associated_model_metadata?.leads_import_id,
                  })}
                >
                  {i18n.t("js.operations.actions.view_in_leads")}
                </a>
              </Button>
            )}
            {operation.kind === "leads/companies_import" && (
              <Button size="xs" className="operation-card__action" asChild>
                <a
                  href={
                    operation.associated_model_metadata?.id
                      ? leads_company_list_path(
                          operation.associated_model_metadata?.id!,
                          {
                            companies_import_id:
                              operation.associated_model_metadata
                                ?.companies_import_id,
                          },
                        )
                      : leads_companies_path({
                          companies_import_id:
                            operation.associated_model_metadata
                              ?.companies_import_id,
                        })
                  }
                >
                  {i18n.t("js.operations.actions.view_in_leads")}
                </a>
              </Button>
            )}
            {["leads/export", "leads/companies_export"].includes(
              operation.kind,
            ) &&
              operation.associated_model_metadata?.blob_url && (
                <Button size="xs" className="operation-card__action" asChild>
                  <a href={operation.associated_model_metadata?.blob_url}>
                    {i18n.t("js.operations.actions.download")}
                  </a>
                </Button>
              )}
            {["bulk/verification", "bulk/search", "bulk/finder"].includes(
              operation.kind,
            ) && (
              <Button size="xs" className="operation-card__action" asChild>
                <a href={operation.associated_model_metadata?.url}>
                  {i18n.t("js.operations.actions.view_bulk")}
                </a>
              </Button>
            )}
          </OperationsCardActions>
        </OperationsCardHeader>
        <OperationsCardDetails>
          <OperationsCardDetailsActions>
            {operation.kind == "leads/import" && (
              <Button size="sm" asChild>
                <a
                  href={leads_import_path(
                    operation.associated_model_metadata.leads_import_id,
                  )}
                >
                  {i18n.t("js.operations.card.see_import_details")}
                </a>
              </Button>
            )}
            {operation.kind == "leads/companies_import" && (
              <Button size="sm" asChild>
                <a
                  href={companies_import_path(
                    operation.associated_model_metadata.companies_import_id,
                  )}
                >
                  {i18n.t("js.operations.card.see_import_details")}
                </a>
              </Button>
            )}
            {cancellableOperations.includes(operation.kind) &&
              (operation.status === "queued" ||
                operation.status === "started") && (
                <Button
                  size="sm"
                  variant="dangerLight"
                  onClick={() => {
                    fetch(operation_cancel_path(operation.id), {
                      method: "DELETE",
                      headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token":
                          document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute("content") || "",
                      },
                    })
                  }}
                >
                  {i18n.t("js.operations.card.cancel_operation")}
                </Button>
              )}
            {/* Special case for bulk verification */}
            {operation.kind === "bulk/verification" &&
              (operation.status === "queued" ||
                operation.status === "started") && (
                <Button
                  size="sm"
                  variant="dangerLight"
                  onClick={() => {
                    fetch(
                      cancel_bulk_verification_path(
                        operation.associated_model_metadata?.id!,
                      ),
                      {
                        method: "DELETE",
                        headers: {
                          "Content-Type": "application/json",
                          "X-CSRF-Token":
                            document
                              .querySelector('meta[name="csrf-token"]')
                              ?.getAttribute("content") || "",
                        },
                      },
                    )
                  }}
                >
                  {i18n.t("js.operations.card.cancel_operation")}
                </Button>
              )}
          </OperationsCardDetailsActions>
        </OperationsCardDetails>
      </OperationsCard>
    </OperationProvider>
  )
}

const OperationsPanel = () => {
  const [activeTab, setActiveTab] = useState("all")
  const [open, setOpen] = useState(false)
  const [operations, setOperations] = useState<Operation[]>([])

  const markOperationAsRead = async (operationId: number) => {
    try {
      const response = await fetch(operation_path(operationId), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
        body: JSON.stringify({ read_at: true }),
      })
      if (response.ok) {
        setOperations((prev) =>
          prev.map((op) =>
            op.id === operationId ? { ...op, read_at: new Date() } : op,
          ),
        )
      }
    } catch (error) {
      console.error("Failed to mark operation as read:", error)
    }
  }

  const markAllAsRead = async (operationIds: number[]) => {
    try {
      const response = await fetch(operations_read_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
        body: JSON.stringify({ ids: operationIds }),
      })
      if (response.ok) {
        setOperations((prev) =>
          prev.map((op) =>
            operationIds.includes(op.id) ? { ...op, read_at: new Date() } : op,
          ),
        )
      }
    } catch (error) {
      console.error("Failed to mark operations as read:", error)
    }
  }

  useEffect(() => {
    fetch("/operations")
      .then((response) => response.json())
      .then((data) => {
        setOperations(data)
      })
  }, [])

  useEffect(() => {
    const consumer = ActionCable.createConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: "OperationsChannel" },
      {
        received: (data: { operation: Operation }) => {
          const newOperation = data.operation
          setOperations((prev) => {
            const oldOperation = prev.find(
              (operation) => operation.id === data.operation.id,
            )
            if (oldOperation) {
              if (oldOperation.processed_count > newOperation.processed_count) {
                // Operation we already have is already more advanced, so we don't need to update it
                return prev
              }

              // Update the operation in the list
              return prev.map((oldOperation) =>
                oldOperation.id === newOperation.id
                  ? newOperation
                  : oldOperation,
              )
            } else {
              // Add the operation to the list
              return [...prev, data.operation]
            }
          })
        },
      },
    )

    return () => {
      subscription.unsubscribe()
      consumer.disconnect()
    }
  }, [])

  const sortedOperations = useMemo(() => {
    return operations.sort(
      (a, b) =>
        new Date(b.queued_at).getTime() - new Date(a.queued_at).getTime(),
    )
  }, [operations])

  const activeOperations = useMemo(() => {
    return sortedOperations.filter(
      (operation) =>
        operation.status === "started" || operation.status === "queued",
    )
  }, [sortedOperations])

  const completedOperations = useMemo(() => {
    return sortedOperations.filter(
      (operation) => operation.status === "finished",
    )
  }, [sortedOperations])

  const unsuccesfulOperations = useMemo(() => {
    return sortedOperations.filter(
      (operation) =>
        operation.status === "failed" || operation.status === "canceled",
    )
  }, [sortedOperations])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="support-menu__link" type="button">
          <span className="support-menu__icon">
            <span className="far fa-signal-stream" aria-hidden="true"></span>
          </span>
          <span className="support-menu__label">
            {i18n.t("js.operations.popover_title")}
          </span>
          {activeOperations.length > 0 && (
            <span
              className="support-menu__live-indicator"
              data-operations-panel-target="liveIndicator"
            ></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        className="operations-panel m-4 animate-slide"
      >
        <header className="operations-panel__header">
          <h2 className="operations-panel__title">
            {i18n.t("js.operations.popover_title")}
            <button
              className="question-circle"
              data-controller="tooltip"
              data-tooltip-close-on-click-outside-value="true"
              data-bs-trigger="click"
              data-bs-custom-class="operations-panel__tooltip"
              data-bs-html="true"
              title={i18n.t("js.operations.tooltip")}
            >
              <Icon icon="question-circle" size="sm" />
            </button>
          </h2>
          <button
            className="h-button h-button--xs h-button--ghost h-button--icon"
            type="button"
            data-action="click->operations-panel#close"
            data-operations-panel-force-param="true"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </header>
        <Tabs value={activeTab}>
          <TabsList color="lightgrey" defaultValue="all" className="px-4">
            <TabsListItem>
              <TabsTrigger value="all" onClick={() => setActiveTab("all")}>
                {i18n.t("js.operations.tabs.all")}
                <TabsTriggerCount>{operations.length}</TabsTriggerCount>
              </TabsTrigger>
            </TabsListItem>

            <TabsListItem>
              <TabsTrigger
                value="active"
                onClick={() => setActiveTab("active")}
              >
                {i18n.t("js.operations.tabs.active")}
                <TabsTriggerCount>{activeOperations.length}</TabsTriggerCount>
              </TabsTrigger>
            </TabsListItem>
            <TabsListItem>
              <TabsTrigger
                value="successful"
                onClick={() => setActiveTab("successful")}
              >
                {i18n.t("js.operations.tabs.successful")}
                <TabsTriggerCount>
                  {completedOperations.length}
                </TabsTriggerCount>
              </TabsTrigger>
            </TabsListItem>
            <TabsListItem>
              <TabsTrigger
                value="unsuccessful"
                onClick={() => setActiveTab("unsuccessful")}
              >
                {i18n.t("js.operations.tabs.unsuccessful")}
                <TabsTriggerCount>
                  {unsuccesfulOperations.length}
                </TabsTriggerCount>
              </TabsTrigger>
            </TabsListItem>
          </TabsList>
          <TabsContent value="all">
            {operations.length === 0 && <EmptyState type="all" />}
            {operations.length > 0 && (
              <div className="operations-panel__content">
                {operations.map((operation) =>
                  renderOperationCard(operation, (expanded) => {
                    if (expanded && !operation.read_at) {
                      markOperationAsRead(operation.id)
                    }
                  }),
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent value="active">
            {activeOperations.length === 0 && <EmptyState type="active" />}
            {activeOperations.length > 0 && (
              <div className="operations-panel__content">
                {activeOperations.map((operation) =>
                  renderOperationCard(operation, (expanded) => {
                    if (expanded && !operation.read_at) {
                      markOperationAsRead(operation.id)
                    }
                  }),
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent value="successful">
            {completedOperations.length === 0 && (
              <EmptyState type="successful" />
            )}
            {completedOperations.length > 0 && (
              <div className="operations-panel__content">
                {completedOperations.map((operation) =>
                  renderOperationCard(operation, (expanded) => {
                    if (expanded && !operation.read_at) {
                      markOperationAsRead(operation.id)
                    }
                  }),
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent value="unsuccessful">
            {unsuccesfulOperations.length === 0 && (
              <EmptyState type="unsuccessful" />
            )}
            {unsuccesfulOperations.length > 0 && (
              <div className="operations-panel__content">
                {unsuccesfulOperations.map((operation) =>
                  renderOperationCard(operation, (expanded) => {
                    if (expanded && !operation.read_at) {
                      markOperationAsRead(operation.id)
                    }
                  }),
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
        {(() => {
          let tabOperations: Operation[] = []
          if (activeTab === "all") {
            tabOperations = operations
          } else if (activeTab === "active") {
            tabOperations = activeOperations
          } else if (activeTab === "successful") {
            tabOperations = completedOperations
          } else if (activeTab === "unsuccessful") {
            tabOperations = unsuccesfulOperations
          }

          if (tabOperations.length === 0) {
            return null
          }

          // Check if there are any unread operations in the current tab
          const hasUnreadOperations = tabOperations.some((op) => !op.read_at)

          if (!hasUnreadOperations) {
            return null
          }

          return (
            <footer className="operations-panel__footer">
              <button
                className="btn-empty operations-panel__action"
                onClick={() => {
                  const unreadIds = tabOperations
                    .filter((op) => !op.read_at)
                    .map((op) => op.id)
                  if (unreadIds.length > 0) {
                    markAllAsRead(unreadIds)
                  }
                }}
              >
                <span className="far fa-check-double" aria-hidden="true"></span>
                {i18n.t("js.operations.actions.mark_tab_as_read", {
                  tab: activeTab,
                })}
              </button>
            </footer>
          )
        })()}
      </PopoverContent>
    </Popover>
  )
}

export default OperationsPanel
